export default function PrivacyContent() {
  return (
    <>
      <p>
        <strong>Last Updated</strong>: September 2, 2026
      </p>

      <p>
        This Privacy Policy explains how License Lounge, Inc., a Delaware
        corporation doing business as Lutely (&ldquo;<strong>Lutely</strong>
        ,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;)
        collects, uses, discloses, retains, and protects personal information
        when you access or use the Lutely website, applications, marketplace,
        artificial-intelligence and audio-processing tools, communications, and
        related services (collectively, the &ldquo;<strong>Platform</strong>
        &rdquo;).
      </p>

      <p>
        This Policy applies to listeners, buyers, artists, labels, sellers,
        account administrators, visitors, and other Users. Capitalized terms not
        defined here have the meanings given in the Lutely{" "}
        <a href="/terms">Terms of Use</a>.
      </p>

      <h2>1. Personal information we collect</h2>
      <p>The information we collect depends on how you use the Platform.</p>

      <h3>1.1 Account and identity information</h3>
      <p>
        We may collect your name, email address, telephone number, username,
        password or authentication identifiers, date of birth or age
        information, profile image, address, organization, artist or label
        affiliation, and account preferences.
      </p>

      <h3>1.2 Artist, label, and rights information</h3>
      <p>
        From Artists and their representatives, we may collect artist names,
        biographies, photographs, logos, roster information, label or
        distributor relationships, authority documentation, ownership and
        licensing information, contributor and rights-holder information,
        publishing and composition information, songwriter and publisher
        information, affiliations with performing-rights, collective-management,
        mechanical-rights, and similar rights organizations, related membership
        or account identifiers, and records used to verify rights or identity.
      </p>

      <h3>1.3 Artist Content and files</h3>
      <p>
        We collect sound recordings, musical compositions, artwork, photographs,
        lyrics, metadata, videos, prompts, instructions, project files, and
        other content you upload, create, process, publish, or store through the
        Platform. We may also collect Outputs created through Lutely Tools.
      </p>

      <h3>1.4 Purchase and transaction information</h3>
      <p>
        We collect information about items viewed, streamed, added to a cart,
        purchased, downloaded, refunded, disputed, or charged back; order
        identifiers; purchase dates; prices; service fees; payment-processing
        fees; discounts; taxes; currencies; Artist allocations; and transaction
        status.
      </p>

      <h3>1.5 Payment, payout, and Stripe information</h3>
      <p>
        Payments and Artist payouts are processed through Stripe or other
        payment providers. Those providers may collect payment-card numbers,
        bank-account information, government identification, tax information,
        and verification documents directly from you. Stripe processes personal
        information in accordance with its own privacy policy, available at{" "}
        <a
          href="https://stripe.com/privacy"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://stripe.com/privacy
        </a>
        . Your provision of payment, payout, identity-verification, tax, or
        other information directly to Stripe is subject to Stripe&rsquo;s
        privacy practices and applicable agreements with Stripe.
      </p>
      <p>
        Lutely may receive payment tokens, limited card details such as brand
        and last four digits, billing information, connected-account
        identifiers, onboarding and verification status, balances, payout
        information, refund and dispute information, risk signals, and other
        transaction records. Lutely generally does not receive or store complete
        payment-card numbers.
      </p>

      <h3>1.6 Listening, download, and marketplace activity</h3>
      <p>
        We may collect searches, page views, previews, streams, playback
        activity, library activity, downloads, redownloads, follows, favorites,
        purchases, cart activity, and interactions with Artist pages and
        releases.
      </p>

      <h3>1.7 Communications and community information</h3>
      <p>
        We collect messages, support requests, feedback, survey responses,
        reports, complaints, artist-to-buyer communications sent through the
        Platform, marketing preferences, opt-out requests, and other
        communications with Lutely or other Users.
      </p>

      <h3>1.8 Device, usage, and technical information</h3>
      <p>
        We may collect internet-protocol address, device and browser type,
        operating system, language, referring pages, approximate location
        derived from IP address, identifiers, cookie and similar-technology
        data, log data, crash data, performance data, security events, and
        information about how you interact with the Platform.
      </p>

      <h3>1.9 Information from third parties</h3>
      <p>
        We may receive information from Stripe, identity and authentication
        providers, analytics providers, fraud-prevention vendors, cloud and
        hosting providers, advertising platforms, social networks, rights
        holders, copyright claimants, other Users, public sources, and business
        partners.
      </p>

      <h3>1.10 De-Identified and Aggregate Information</h3>
      <p>
        We may also generate, collect, use, and disclose aggregated,
        de-identified, or otherwise anonymized information that does not
        reasonably identify you, subject to applicable law.
      </p>

      <h2>2. How we use personal information</h2>
      <p>We may use personal information to:</p>
      <ul>
        <li>create and administer accounts;</li>
        <li>
          authenticate Users and verify identity, authority, and rights
          ownership;
        </li>
        <li>
          provide, personalize, operate, maintain, troubleshoot, and improve the
          Platform;
        </li>
        <li>process User Content and generate Outputs through Lutely Tools;</li>
        <li>publish Artist pages and releases;</li>
        <li>
          process purchases, service fees, transfers, payouts, refunds, and
          taxes;
        </li>
        <li>
          provide streaming, downloads, redownloads, libraries, receipts, and
          customer support;
        </li>
        <li>calculate Artist Proceeds, analytics, and marketplace reports;</li>
        <li>
          share transaction information with the applicable Artist as described
          below;
        </li>
        <li>
          communicate about accounts, purchases, subscriptions, security,
          product updates, and policies;
        </li>
        <li>
          provide marketing and promotional communications in accordance with
          applicable law and your choices;
        </li>
        <li>
          detect, investigate, prevent, and respond to fraud, payment abuse,
          infringement, security incidents, prohibited content, and violations
          of our policies;
        </li>
        <li>
          enforce agreements, resolve disputes, collect amounts owed, and
          protect rights and safety;
        </li>
        <li>
          comply with legal, regulatory, tax, accounting, reporting, sanctions,
          and law-enforcement obligations;
        </li>
        <li>
          conduct analytics, research, testing, quality assurance, and product
          development; and
        </li>
        <li>
          support corporate transactions such as financing, merger, acquisition,
          reorganization, or sale.
        </li>
      </ul>

      <h2>3. Artificial intelligence and content processing</h2>
      <p>
        Lutely may use User Content, prompts, Outputs, and associated usage
        information to operate, secure, test, maintain, evaluate, develop, and
        improve Lutely Tools and related technology, including through model
        training, only to the extent permitted by applicable law and the
        agreements governing your use of the Platform. Where practicable, Lutely
        may use de-identified, aggregated, or otherwise minimized information
        for these purposes. Lutely may exclude, restrict, or remove particular
        content from these activities to address rights claims, user choices,
        safety concerns, legal requirements, or operational needs.
      </p>
      <p>
        Lutely may permit contracted service providers, including cloud hosting,
        storage, security, and technical support providers, to process User
        Content and related information solely to provide services to Lutely and
        subject to contractual confidentiality and use restrictions. Lutely does
        not provide User Content, prompts, or Outputs to third-party model
        providers for their independent training of their models without notice
        or an appropriate contractual basis.
      </p>
      <p>
        Lutely will not knowingly use Artist Content to create or commercially
        market an AI generated voice replica intended to imitate the
        identifiable voice of an Artist without that Artist&rsquo;s express
        authorization. This statement does not guarantee that Outputs will not
        be similar to other content or that third parties will not misuse
        content outside Lutely&rsquo;s control.
      </p>
      <p>
        Do not upload User Content, including any personal information, that you
        do not have authority to provide and process.
      </p>

      <h2>4. How marketplace information is shared with Artists</h2>
      <p>
        When you purchase Artist Content through the Platform, Lutely may
        disclose information to the applicable Artist or the Artist&rsquo;s
        authorized label, manager, distributor, or account administrators. This
        information may include:
      </p>
      <ul>
        <li>your name;</li>
        <li>your email address;</li>
        <li>the Artist Content you purchased;</li>
        <li>the purchase price and transaction date;</li>
        <li>relevant fees, order status, and refund or dispute status; and</li>
        <li>
          other information reasonably necessary to support the transaction and
          the Artist&rsquo;s relationship with you.
        </li>
      </ul>
      <p>
        Artists may use this information for customer service, purchase support,
        release news, artist updates, and lawful direct communications. Artists
        are prohibited by the Terms of Use from selling, renting, or providing
        Buyer information to unrelated third parties and must comply with
        applicable privacy and marketing laws and honor opt-out requests.
      </p>
      <p>
        Artists may download or retain Buyer information outside Lutely. Once
        information has been provided to an Artist, Lutely cannot guarantee that
        it will be deleted from every system controlled by that Artist. You may
        contact the Artist directly regarding the Artist&rsquo;s independent use
        of your information and may also contact Lutely for assistance.
      </p>
      <p>
        A multi-Artist order will result in each Artist receiving only the
        information reasonably related to the items attributed to that Artist.
      </p>

      <h2>5. Other ways we disclose personal information</h2>

      <h3>5.1 Service providers</h3>
      <p>
        We may disclose information to vendors that provide payment processing,
        cloud hosting, marketing, content delivery, storage, analytics,
        authentication, communications, customer support, security, fraud
        prevention, tax, accounting, legal, and other services. These providers
        may process information only for the purposes for which it is provided,
        subject to their contracts and applicable law.
      </p>

      <h3>5.2 Payment and financial partners</h3>
      <p>
        We may disclose information to Stripe and other banks, processors,
        payment networks, connected accounts, and financial partners involved in
        collecting payments, making transfers, verifying identities, preventing
        fraud, resolving disputes, and complying with financial laws. Stripe
        processes personal information in accordance with its own privacy
        policy, available at{" "}
        <a
          href="https://stripe.com/privacy"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://stripe.com/privacy
        </a>
        .
      </p>

      <h3>5.3 Other Users and the public</h3>
      <p>
        Profile information, Artist pages, published Artist Content, listings,
        usernames, comments, and other information you choose to make public may
        be visible to other Users and the public. Information posted publicly
        may be copied, indexed, or redistributed by others.
      </p>

      <h3>5.4 Legal, safety, and enforcement disclosures</h3>
      <p>
        We may disclose information where we believe in good faith that
        disclosure is necessary to comply with law, legal process, court order,
        tax or regulatory requirements, or a valid governmental request; enforce
        our agreements; investigate fraud or infringement; collect amounts owed;
        protect Lutely, Users, payment providers, rights holders, or the public;
        or prevent harm.
      </p>

      <h3>5.5 Business transfers</h3>
      <p>
        We may disclose or transfer information in connection with an actual or
        proposed financing, merger, acquisition, reorganization, bankruptcy,
        sale of assets, or other corporate transaction, subject to appropriate
        confidentiality protections where required.
      </p>

      <h3>5.6 With your direction or consent</h3>
      <p>
        We may disclose personal information when you direct or authorize us to
        do so, including when you connect or use a third-party service through
        the Platform.
      </p>

      <h2>6. Cookies, analytics, and similar technologies</h2>
      <p>
        When you visit our website or services, we and our service providers may
        automatically collect information about your device and use of the
        website or services, including your IP address, device and advertising
        identifiers, browser type and language, operating system, internet
        service provider, session identifiers, network-connected hardware
        information, pages viewed, links clicked, actions taken, and dates and
        times of activity.
      </p>
      <p>
        We use this information to operate, secure, authenticate, analyze,
        improve, and personalize the website and our services; remember your
        device, preferences, and interactions; associate devices; detect and
        prevent fraud; measure website, service, and advertising performance;
        and market our services and deliver content and advertisements that may
        be relevant to you.
      </p>
      <p>
        We and our service providers may use cookies, pixels, web beacons,
        embedded links, software development kits, log files, and similar
        technologies for these purposes. Cookies may be session cookies, which
        generally expire when you close your browser, or persistent cookies,
        which remain on your device until deleted or expired.
      </p>
      <p>
        We may use third-party analytics and advertising providers to help us
        understand website and service usage, measure the effectiveness of
        advertising, and deliver interest-based advertising on the website and
        other online services. These providers may collect information about
        your use of the website, communications, advertisements, and other
        online services and may process that information under their own privacy
        notices. Our website and services may use{" "}
        <a
          href="https://www.adobe.com/privacy/policy.html"
          target="_blank"
          rel="noopener noreferrer"
        >
          Adobe
        </a>
        ,{" "}
        <a
          href="https://privacy.microsoft.com/en-us/privacystatement"
          target="_blank"
          rel="noopener noreferrer"
        >
          Bing
        </a>
        ,{" "}
        <a
          href="https://www.facebook.com/privacy/policy"
          target="_blank"
          rel="noopener noreferrer"
        >
          Facebook Connect
        </a>
        ,{" "}
        <a
          href="https://policies.google.com/technologies/partner-sites"
          target="_blank"
          rel="noopener noreferrer"
        >
          Google Analytics
        </a>
        ,{" "}
        <a
          href="https://policies.google.com/privacy"
          target="_blank"
          rel="noopener noreferrer"
        >
          Google Audiences
        </a>
        ,{" "}
        <a
          href="https://userback.io/privacy/"
          target="_blank"
          rel="noopener noreferrer"
        >
          userback
        </a>
        ,{" "}
        <a
          href="https://www.facebook.com/privacy/policy"
          target="_blank"
          rel="noopener noreferrer"
        >
          Meta
        </a>
        ,{" "}
        <a
          href="https://www.linkedin.com/legal/privacy-policy"
          target="_blank"
          rel="noopener noreferrer"
        >
          LinkedIn
        </a>
        ,{" "}
        <a
          href="https://policy.pinterest.com/en/privacy-policy"
          target="_blank"
          rel="noopener noreferrer"
        >
          Pinterest
        </a>
        ,{" "}
        <a
          href="https://snap.com/en-US/privacy/privacy-policy"
          target="_blank"
          rel="noopener noreferrer"
        >
          Snapchat
        </a>
        ,{" "}
        <a
          href="https://www.tiktok.com/legal/privacy-policy-us"
          target="_blank"
          rel="noopener noreferrer"
        >
          TikTok
        </a>
        ,{" "}
        <a
          href="https://x.com/en/privacy"
          target="_blank"
          rel="noopener noreferrer"
        >
          X
        </a>
        , and others for audience measurement and analytics purposes. You may
        review Google&rsquo;s{" "}
        <a
          href="https://support.google.com/analytics/answer/6004245"
          target="_blank"
          rel="noopener noreferrer"
        >
          data privacy practices for Google Analytics
        </a>{" "}
        and data privacy practices for{" "}
        <a
          href="https://support.google.com/tagmanager/answer/9323295"
          target="_blank"
          rel="noopener noreferrer"
        >
          Google Tag Manager
        </a>
        . To opt out of being tracked by Google Analytics, you may download and
        install the{" "}
        <a
          href="https://tools.google.com/dlpage/gaoptout"
          target="_blank"
          rel="noopener noreferrer"
        >
          Google Analytics Opt-out Browser Add-on
        </a>
        .
      </p>
      <p>
        We may use service providers and partners to serve advertisements on our
        behalf on the website and services and across the internet, and to track
        and report on the performance of those advertisements. These service
        providers may include{" "}
        <a
          href="https://www.adobe.com/privacy/policy.html"
          target="_blank"
          rel="noopener noreferrer"
        >
          Adobe
        </a>
        ,{" "}
        <a
          href="https://policies.google.com/privacy"
          target="_blank"
          rel="noopener noreferrer"
        >
          Google Ads
        </a>{" "}
        (formerly Google Adwords),{" "}
        <a
          href="https://privacy.microsoft.com/en-us/privacystatement"
          target="_blank"
          rel="noopener noreferrer"
        >
          Microsoft Advertising
        </a>{" "}
        (formerly Bing Ads),{" "}
        <a
          href="https://www.facebook.com/privacy/policy"
          target="_blank"
          rel="noopener noreferrer"
        >
          Facebook
        </a>
        ,{" "}
        <a
          href="https://www.linkedin.com/legal/privacy-policy"
          target="_blank"
          rel="noopener noreferrer"
        >
          LinkedIn
        </a>
        ,{" "}
        <a
          href="https://policy.pinterest.com/en/privacy-policy"
          target="_blank"
          rel="noopener noreferrer"
        >
          Pinterest
        </a>
        ,{" "}
        <a
          href="https://snap.com/en-US/privacy/privacy-policy"
          target="_blank"
          rel="noopener noreferrer"
        >
          Snapchat
        </a>
        ,{" "}
        <a
          href="https://www.tapad.com/privacy"
          target="_blank"
          rel="noopener noreferrer"
        >
          Tapad
        </a>
        ,{" "}
        <a
          href="https://x.com/en/privacy"
          target="_blank"
          rel="noopener noreferrer"
        >
          X
        </a>
        , and others.
      </p>
      <p>
        Of course, if you do not wish to have cookies on your devices, you may
        turn them off at any time by modifying your internet browser&rsquo;s or
        device&rsquo;s settings. Please refer to the help section of your
        browser or mobile device for additional information. Your browser or
        mobile device may also offer add-ons, plugins, or extensions to manage
        cookies, local storage objects, scripts, or similar technologies
        mentioned in this Privacy Policy. Apple and Android mobile devices each
        generate an advertising identifier that can be accessed by apps and used
        by advertisers in much the same way that cookies are used on websites.
        Each operating system provides options to limit tracking and/or reset
        the advertising ID. However, by disabling cookies on your browser or
        device, you may be prohibited from full use of the website&rsquo;s or
        services&rsquo; features or lose access to some functionality.
      </p>
      <p>
        You may opt out of receiving interest-based advertisements from other
        companies that perform interest-based advertising services who are
        members of the Digital Advertising Alliance (DAA) by visiting the{" "}
        <a
          href="https://optout.aboutads.info/"
          target="_blank"
          rel="noopener noreferrer"
        >
          DAA website
        </a>{" "}
        or{" "}
        <a
          href="https://youradchoices.com/control"
          target="_blank"
          rel="noopener noreferrer"
        >
          YourAdChoices
        </a>
        . If you are located in Canada, you can visit the{" "}
        <a
          href="https://youradchoices.ca/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Digital Advertising Alliance of Canada
        </a>
        . If you are located in Europe, please visit the{" "}
        <a
          href="https://www.youronlinechoices.com/"
          target="_blank"
          rel="noopener noreferrer"
        >
          European Digital Advertising Alliance
        </a>
        . Please note that when using the ad industry opt-out tools described
        above, you may need to execute opt-outs for each browser or device that
        you use. Opting out of interest-based advertising does not mean you will
        no longer see advertising online, but it does mean that the companies
        from which you opt out will no longer show ads that have been tailored
        to your interests. If you use industry opt-out tools, your opt out will
        only apply to companies who are participating in those industry
        organization tools.
      </p>

      <h2>7. Marketing and communications</h2>
      <p>
        Lutely may send transactional communications concerning purchases,
        account activity, security, subscriptions, policy changes, and service
        announcements. You generally cannot opt out of communications necessary
        to provide the Platform or administer a transaction.
      </p>
      <p>
        Lutely may send promotional communications where permitted by law. You
        may unsubscribe using the link in an email or by following the
        instructions in the message. Opting out of Lutely marketing does not
        automatically opt you out of communications sent independently by an
        Artist.
      </p>
      <p>
        Artists may contact Buyers using information disclosed after a purchase,
        subject to the Terms of Use and applicable law. Buyers may opt out
        directly from an Artist&rsquo;s promotional communications. Lutely may
        suspend an Artist that fails to honor opt-out requests or misuses Buyer
        information.
      </p>

      <h2>8. Data retention</h2>
      <p>
        We retain personal information for as long as reasonably necessary to
        provide the Platform, maintain purchase access and transaction records,
        administer Artist payouts, resolve disputes, prevent fraud, enforce
        agreements, comply with tax, accounting, copyright, and legal
        obligations, and support legitimate business purposes.
      </p>
      <p>Retention periods vary by data type. For example:</p>
      <ul>
        <li>
          account and profile information may be retained while the account
          remains active and for a reasonable period afterward;
        </li>
        <li>
          purchase records, Artist allocations, payment records, tax records,
          refunds, disputes, and chargebacks may be retained for legally
          required and fraud-prevention periods;
        </li>
        <li>
          Artist Content may be retained after removal to support valid prior
          purchases, legal claims, backups, or compliance;
        </li>
        <li>
          support and legal communications may be retained as needed to document
          and resolve an issue; and
        </li>
        <li>
          deidentified or aggregated information may be retained without the
          same limits where permitted by law.
        </li>
      </ul>
      <p>
        Deletion from active systems may not immediately remove information from
        backups, archives, legal holds, payment-provider systems, or copies
        already disclosed to Artists or other third parties.
      </p>

      <h2>
        9. Access, Correct and Delete Personal Information; Account Deletion
      </h2>
      <p>
        If we provide you the option to create an account, you can access,
        correct, or delete some of the personal information we have collected or
        that you have provided to us by logging into your account and accessing
        the information displayed in your profile or account settings. If you do
        not have an account with us, please contact us at{" "}
        <a href="mailto:privacy@lutely.com">privacy@lutely.com</a> to request
        the access, correction, or deletion of your personal information. Before
        we can process your request regarding your personal information, we may
        need to verify your identity to ensure the security and integrity of
        your personal information. Additionally, certain laws and regulations
        may require us to retain specific personal information or limit the
        scope of your request, even after verification. In such cases, we will
        inform you about any restrictions that apply.
      </p>
      <p>
        We will not unlawfully discriminate against you for exercising a privacy
        right. Some requests may be denied or limited where an exception
        applies, including where information is needed to complete transactions,
        maintain purchased access, prevent fraud, comply with law, protect
        rights, or establish or defend legal claims.
      </p>
      <p>
        You may request account deletion through available account controls or
        by contacting <a href="mailto:support@lutely.com">support@lutely.com</a>
        . We may verify your identity before acting.
      </p>
      <p>
        Deleting an account may end access to purchased streaming, redownloads,
        Artist dashboards, projects, or other account features. We may retain
        information where necessary to maintain valid purchases, complete
        transactions, administer payouts, comply with law, prevent fraud,
        resolve disputes, enforce agreements, or protect rights and safety.
      </p>
      <p>
        If you are an Artist, account deletion does not eliminate obligations
        relating to prior sales, refunds, disputes, chargebacks, taxes, rights
        claims, Buyer access, or amounts owed.
      </p>

      <h2>10. Security</h2>
      <p>
        Lutely uses administrative, technical, and physical safeguards designed
        to protect personal information. No security method is perfect, and we
        cannot guarantee absolute security.
      </p>
      <p>
        You are responsible for using a strong password, enabling available
        security features, protecting your devices and credentials, and
        notifying us promptly of suspected unauthorized access.
      </p>

      <h2>11. Additional rights under state law</h2>
      <p>
        <strong>California.</strong>
      </p>
      <ul>
        <li>
          <strong>Shine the Light law.</strong> California Civil Code Section
          &sect; 1798.83 permits users of our Website that are California
          residents to request certain information regarding our disclosure of
          Personal Information to third parties for their direct marketing
          purposes. To make such a request, please send an email to{" "}
          <a href="mailto:privacy@lutely.com">privacy@lutely.com</a>, or write
          us at License Lounge, Inc. d/b/a Lutely 440 Burroughs Street, Suite
          129 Detroit, Michigan 48202, United States.
        </li>
        <li>
          <strong>Do Not Track Signals.</strong> Other than as disclosed in this
          Privacy Policy, the website and services do not operate any
          differently when it receives Do Not Track signals from your internet
          web browser.
        </li>
      </ul>
      <p>
        <strong>Nevada.</strong> Nevada law permits customers in Nevada to
        opt-out of the sale of certain kinds of personal information. We do not
        sell your personal information to third parties as defined in Nevada
        law. If you are a Nevada resident and have any questions, you can
        contact us at <a href="mailto:privacy@lutely.com">privacy@lutely.com</a>
        .
      </p>

      <h2>12. International Users</h2>
      <p>
        Under the (i) General Data Protection Regulation (Regulation (EU)
        2016/679 of the European Parliament and of the Council of 27 April 2016,
        or &ldquo;GDPR&rdquo;), (ii) Data Protection Act 2018, (iii) the GDPR as
        it forms part of the law of England and Wales, Scotland and Northern
        Ireland (i.e., &ldquo;UK GDPR&rdquo;) as provided in the Data Protection
        Act 2018, and (iv) any other applicable data protection legislation of
        any country or other jurisdiction (collectively &ldquo;International
        Data Protection Laws&rdquo;) individuals have specific rights with
        respect to their personal information, or &ldquo;personal data&rdquo; as
        defined under the International Data Protection Laws. For the purposes
        of this Privacy Policy, the Lutely operates as a data controller. Any
        personal data we collect from you is processed in the United States and
        under the terms of this Policy.
      </p>
      <p>
        Any personal data we collect from you is processed in the legitimate
        interest of our business and providing our services to you as the lawful
        means of such processing. You may always withdraw your consent to our
        use of your personal data as described below. We will only retain your
        personal data for the time necessary to provide you the information and
        services to which you have consented, to comply with the law and in
        accordance with your rights below.
      </p>
      <p>The Data Controller is:</p>
      <p>
        NAME: License Lounge, Inc. d/b/a Lutely
        <br />
        ADDRESS: 440 Burroughs Street, Suite 129 Detroit, Michigan 48202, United
        States
        <br />
        EMAIL ADDRESS:{" "}
        <a href="mailto:privacy@lutely.com">privacy@lutely.com</a>
      </p>
      <p>
        You can exercise any of the following rights, subject to verification of
        your identity, by notifying us as described below:
      </p>
      <ul>
        <li>
          <strong>Access.</strong> You may request a copy of the personal data
          that you have provided to us or that we have processed about you.
        </li>
        <li>
          <strong>Automated Processing and Decision-Making.</strong> You may
          request that we stop using your personal data for automated
          processing, such as profiling.
        </li>
        <li>
          <strong>Correction or Rectification.</strong> You can correct the
          personal data that you have provided to us or that we have processed
          about you by accessing your account directly, or by emailing us (as
          provided below).
        </li>
        <li>
          <strong>Restrict Processing.</strong> When applicable, you may
          restrict the processing of your personal data by submitting a request
          via email (to the email provided below). In your email, please explain
          how you wish us to restrict processing of your personal data. When
          such restrictions are not possible, we will advise you accordingly.
        </li>
        <li>
          <strong>Object to Processing.</strong> When applicable, you have the
          right to object to the processing of your personal data by submitting
          a request via email to (to the email provided below). When such
          objections are not possible, we will advise you accordingly.
        </li>
        <li>
          <strong>Portability.</strong> Upon request and when possible, we can
          provide you with copies of your personal data that you have provided
          to us. When such a request cannot be honored, we will advise you
          accordingly.
        </li>
        <li>
          <strong>Withdraw Consent.</strong> At any time, you may withdraw your
          consent to our processing of your personal data through the website or
          services by notifying us via email (to the email provided below).
        </li>
        <li>
          <strong>Erasure.</strong> If you should wish to cease use of our
          website or services and have your personal data deleted, then you may
          submit a request by emailing us at the email provided below.
        </li>
      </ul>
      <p>
        <strong>Exercising your rights.</strong> If you are a data subject that
        has rights under the International Data Protection Laws, who chooses to
        exercise the rights listed above, you can submit a request via email at{" "}
        <a href="mailto:privacy@lutely.com">privacy@lutely.com</a>.
      </p>
      <p>
        <strong>Submit Complaints or Questions.</strong> If you wish to raise a
        complaint on how we have handled your personal data, you can contact us
        as described below. If you reside in a European Union member state or
        the United Kingdom, you may also lodge a complaint with the supervisory
        authority in your country.
      </p>

      <h2>13. Children&rsquo;s privacy</h2>
      <p>
        The Platform is not directed to children under thirteen (13), and Lutely
        does not knowingly collect personal information from children under
        thirteen. If we learn that a child under thirteen provided personal
        information without legally valid parental authorization, we will take
        reasonable steps to delete it.
      </p>
      <p>
        Users ages thirteen through seventeen may use listener and buyer
        features only with permission and supervision from a parent or legal
        guardian. Artist selling and Stripe Connect features are limited to
        adults or authorized adult representatives of legal entities.
      </p>
      <p>
        Parents or guardians who believe a child provided information improperly
        may contact <a href="mailto:privacy@lutely.com">privacy@lutely.com</a>.
      </p>

      <h2>14. Third-party links and services</h2>
      <p>
        The Platform may link to third-party websites, applications, payment
        services, or social platforms. Their privacy practices are governed by
        their own policies. Lutely is not responsible for third-party privacy or
        security practices.
      </p>

      <h2>15. Changes to this Policy</h2>
      <p>
        Lutely may update this Policy. If a change is material, we may provide
        notice by email, through the Platform, or by another reasonable method.
        The &ldquo;Effective date&rdquo; will identify when the revised Policy
        applies.
      </p>

      <h2>16. Contact us</h2>
      <p>Privacy questions and requests may be sent to:</p>
      <p>
        <a href="mailto:privacy@lutely.com">privacy@lutely.com</a>
      </p>
      <p>
        License Lounge, Inc. d/b/a Lutely 440 Burroughs Street, Suite 129
        Detroit, Michigan 48202, United States
      </p>
    </>
  );
}
